
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function MotionSensorLightingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Motion Sensor Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional motion sensor lighting installation in Fresno, CA. Automatic lights for security, convenience, and energy savings.',
    url: `${siteUrl}/services/lighting-outdoor/motion-sensor-lighting`,
  };

  const features = [
    {
      icon: 'ri-run-line',
      title: 'Automatic Activation',
      description: 'Lights turn on instantly when motion is detected',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Adjustable Sensitivity',
      description: 'Customize detection range and sensitivity for your needs',
    },
    {
      icon: 'ri-timer-line',
      title: 'Programmable Duration',
      description: 'Set how long lights stay on after motion stops',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Energy Savings',
      description: 'Lights only run when needed, reducing electricity costs',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security Enhancement',
      description: 'Deter intruders and illuminate dark areas automatically',
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Hands-Free Convenience',
      description: 'Perfect for carrying groceries or arriving home after dark',
    },
  ];

  return (
    <>
      <SEOHead
        title="Motion Sensor Lighting Fresno CA | Automatic Security Lights"
        description="Professional motion sensor lighting installation in Fresno, CA. Automatic lights for security, convenience, and energy savings. Licensed electricians."
        canonical="/services/lighting-outdoor/motion-sensor-lighting"
        keywords="motion sensor lights Fresno, automatic lighting, motion activated lights, security lighting"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Motion Sensor Lighting — Fresno, CA"
        heroSubtitle="Automatic, energy-efficient motion sensor lighting for Fresno homes and businesses"
        heroImage="https://public.readdy.ai/ai/img_res/57146fac9b9fe1033c825901c327004c.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20motion%20sensor%20light%20fixture%20mounted%20on%20exterior%20wall%20with%20adjustable%20sensor%20head%20and%20bright%20LED%20light%20California%20home%20installation%20no%20people%20present&width=700&height=600&seq=motion-sensor-fresno-007&orientation=portrait"
        fresnoContent="Add convenience and security to your Fresno property with professional motion sensor lighting. From Tower District homes to Woodward Park estates, we install automatic lighting systems that activate when you arrive home, deter intruders, and save energy by only running when needed. Our licensed electricians install adjustable motion sensors with customizable detection range, sensitivity, and duration settings. Perfect for driveways, entryways, garages, backyards, and any area where hands‑free lighting enhances safety and convenience."
        features={features}
        faqs={[
          {
            question: 'How do motion sensor lights work in Fresno homes?',
            answer:
              "Motion sensor lights use passive infrared (PIR) sensors to detect heat signatures from people, animals, or vehicles moving within their detection range. When motion is detected, the light automatically turns on for a preset duration (typically 1–10 minutes), then turns off if no further motion is detected. Modern sensors have adjustable sensitivity to reduce false triggers from small animals or wind-blown debris. In Fresno's climate, we install heat-rated sensors that perform reliably even when ambient temperatures exceed 100°F.",
          },
          {
            question: 'Where should motion sensor lights be installed in Fresno?',
            answer:
              "For optimal coverage in Fresno, we install motion sensor lights at front entryways and porches, garage doors and driveways, back doors and patios, side yards and walkways, and near outdoor storage areas. Sensors should be mounted 6–10 feet high and angled to cover approach paths. We avoid placing sensors near heat sources (AC units, dryer vents) that can cause false triggers. During consultation, we assess your Fresno property and recommend strategic placement for maximum security and convenience.",
          },
          {
            question: 'How much does motion sensor lighting installation cost in Fresno?',
            answer:
              'Motion sensor lighting installation in Fresno typically costs $200–$500 per fixture depending on type and features. Basic motion sensor lights start around $200–$300, while high-output LED floodlights with adjustable sensors range from $350–$500. Whole‑property installations with 4–6 fixtures typically cost $1,200–$2,500. We provide free consultations and detailed quotes for all Fresno projects. Motion sensor lights pay for themselves through energy savings and increased security.',
          },
          {
            question: 'Can motion sensor lights save energy in Fresno?',
            answer:
              'Absolutely! Motion sensor lights save 50–75% on outdoor lighting costs compared to lights left on all night. In Fresno, where outdoor lights might otherwise run 10–12 hours nightly, motion sensors ensure lights only operate when needed—typically 30–60 minutes total per night. For a typical Fresno home with 4 outdoor fixtures, motion sensors save $100–$200 annually on electricity. Combined with energy‑efficient LED bulbs, motion sensor lighting is one of the most cost‑effective home upgrades.',
          },
          {
            question: 'Do motion sensor lights work with LED bulbs in Fresno?',
            answer:
              'Yes! Modern motion sensor lights are fully compatible with LED bulbs and fixtures. In fact, we recommend LED motion sensor lights for Fresno properties—they use 75% less energy, produce minimal heat (important in Central Valley summers), and last 25+ years. LED fixtures also provide instant full brightness when motion is detected, unlike older CFL bulbs that take time to warm up. We install complete LED motion sensor systems throughout Fresno for maximum efficiency and reliability.',
          },
          {
            question: 'Can motion sensor lights be adjusted for sensitivity in Fresno?',
            answer:
              'Yes! All quality motion sensor lights have adjustable settings. We install sensors with adjustable detection range (typically 20–70 feet), sensitivity levels to reduce false triggers from small animals or wind, duration settings (how long lights stay on), and some models include daylight sensors to prevent activation during daytime. During installation, we calibrate sensors for your specific Fresno property conditions and show you how to adjust settings as needed. This ensures optimal performance year‑round.',
          },
        ]}
      />
    </>
  );
}
